import React, { useState, useEffect } from 'react';
import { 
  getRequestsApi, 
  getBrandsApi, 
  getSlotsApi, 
  getCohortsApi, 
  getUsersApi,
  approveRequestApi,
  rejectRequestApi
} from '../../utils/api';
import { formatDate } from '../../utils/helpers';
import { ChevronDown, ChevronUp, Check, X, Trash2, Search, Calendar, Filter } from 'lucide-react';
import CampaignPreview from '../../components/CampaignPreview';

const RequestQueue = ({ showToast }) => {
  const [requests, setRequests] = useState([]);
  const [brands, setBrands] = useState([]);
  const [slots, setSlots] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [rejectionReason, setRejectionReason] = useState({});
  const [showRejectModal, setShowRejectModal] = useState(null);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterBrand, setFilterBrand] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [requestsRes, brandsRes, slotsRes, cohortsRes, usersRes] = await Promise.all([
        getRequestsApi(),
        getBrandsApi(),
        getSlotsApi(),
        getCohortsApi(),
        getUsersApi()
      ]);
      
      const activeRequests = requestsRes.data.filter(req => req.status !== 'Cancelled');
      setRequests(activeRequests);
      setBrands(brandsRes.data);
      setSlots(slotsRes.data);
      setCohorts(cohortsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const isDateInFuture = (dateStr) => {
    const campaignDate = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return campaignDate >= today;
  };

  const handleDeleteCampaign = async (requestId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/requests/${requestId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'Cancelled' })
      });

      if (response.ok) {
        showToast('Campaign cancelled successfully', 'success');
        setDeleteConfirm(null);
        loadData();
      } else {
        throw new Error('Failed to cancel campaign');
      }
    } catch (error) {
      showToast('Failed to cancel campaign', 'error');
    }
  };

  const handleApprove = async (requestId) => {
    try {
      await approveRequestApi(requestId);
      showToast('Request approved successfully!', 'success');
      loadData();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to approve request', 'error');
    }
  };

  const handleReject = async (requestId) => {
    const reason = rejectionReason[requestId] || '';
    
    try {
      await rejectRequestApi(requestId, reason);
      showToast('Request rejected', 'success');
      setShowRejectModal(null);
      setRejectionReason({});
      loadData();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to reject request', 'error');
    }
  };

  const filteredRequests = requests.filter(req => {
    if (filterStatus !== 'all' && req.status.toLowerCase() !== filterStatus) return false;
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const brand = brands.find(b => b.id === req.brand_id);
      const cohort = cohorts.find(c => c.id === req.cohort_id);
      const matchesName = req.campaign_name?.toLowerCase().includes(query);
      const matchesBrand = brand?.name?.toLowerCase().includes(query);
      const matchesCohort = cohort?.name?.toLowerCase().includes(query);
      
      if (!matchesName && !matchesBrand && !matchesCohort) return false;
    }
    
    if (startDate && req.date < startDate) return false;
    if (endDate && req.date > endDate) return false;
    if (filterBrand !== 'all' && req.brand_id !== filterBrand) return false;
    
    return true;
  });

  const pendingCount = requests.filter(r => r.status === 'Pending').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Campaign Requests</h1>
          <p className="text-slate-600">Review and approve requests from brand teams</p>
        </div>
        
        {/* Filter Tabs */}
        <div className="mb-6 flex flex-wrap gap-2">
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
              filterStatus === 'all'
                ? 'bg-teal-600 text-white shadow-md'
                : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
            }`}
            style={filterStatus === 'all' ? { backgroundColor: '#2EB4AC' } : {}}
          >
            All <span className="ml-1.5 opacity-80">({requests.length})</span>
          </button>
          <button
            onClick={() => setFilterStatus('pending')}
            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
              filterStatus === 'pending'
                ? 'bg-amber-500 text-white shadow-md'
                : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
            }`}
          >
            Pending <span className="ml-1.5 opacity-80">({pendingCount})</span>
          </button>
          <button
            onClick={() => setFilterStatus('approved')}
            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
              filterStatus === 'approved'
                ? 'bg-green-500 text-white shadow-md'
                : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
            }`}
          >
            Approved
          </button>
          <button
            onClick={() => setFilterStatus('rejected')}
            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
              filterStatus === 'rejected'
                ? 'bg-red-500 text-white shadow-md'
                : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
            }`}
          >
            Rejected
          </button>
        </div>
        
        {/* Search and Filters */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by campaign, brand, or cohort..."
              className="w-full h-11 pl-10 pr-4 bg-white text-slate-900 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all placeholder:text-slate-400"
            />
          </div>
          
          {/* Start Date */}
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full h-11 pl-10 pr-4 bg-white text-slate-900 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
            />
          </div>
          
          {/* End Date */}
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full h-11 pl-10 pr-4 bg-white text-slate-900 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
            />
          </div>
          
          {/* Brand Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
            <select
              value={filterBrand}
              onChange={(e) => setFilterBrand(e.target.value)}
              className="w-full h-11 pl-10 pr-4 bg-white text-slate-900 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all appearance-none cursor-pointer"
            >
              <option value="all">All Brands</option>
              {brands.map(brand => (
                <option key={brand.id} value={brand.id}>{brand.name}</option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Requests List */}
        {filteredRequests.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Search className="text-slate-400" size={28} />
            </div>
            <p className="text-slate-600 font-medium">
              {requests.length === 0 ? 'No campaign requests yet' : 'No requests match your filters'}
            </p>
            <p className="text-slate-400 text-sm mt-1">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredRequests.map((request) => {
              const brand = brands.find(b => b.id === request.brand_id);
              const slot = slots.find(s => s.id === request.slot_id);
              const cohort = cohorts.find(c => c.id === request.cohort_id);
              const user = users.find(u => u.id === request.user_id);
              const isExpanded = expandedRequest === request.id;

              const statusConfig = {
                Pending: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
                Approved: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
                Rejected: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' }
              };

              const status = statusConfig[request.status] || statusConfig.Pending;

              return (
                <div key={request.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md transition-shadow">
                  {/* Header */}
                  <div
                    className="p-5 cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => setExpandedRequest(isExpanded ? null : request.id)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4 flex-1 min-w-0">
                        {/* Creative thumbnail */}
                        {request.creative && (
                          <img
                            src={request.creative}
                            alt="Campaign creative"
                            className="w-20 h-20 object-cover rounded-lg border border-slate-200 flex-shrink-0"
                          />
                        )}
                        
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-3">
                            <h3 className="text-lg font-bold text-slate-900 truncate">
                              {request.campaign_name || `${brand?.name} Campaign`}
                            </h3>
                            <span className={`px-3 py-1 rounded-full border text-xs font-semibold flex-shrink-0 ${status.bg} ${status.text} ${status.border}`}>
                              {request.status}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-slate-500 text-xs font-medium mb-0.5">Requested by</p>
                              <p className="text-slate-900 font-semibold truncate">{user?.name}</p>
                            </div>
                            <div>
                              <p className="text-slate-500 text-xs font-medium mb-0.5">Date</p>
                              <p className="text-slate-900 font-semibold">{formatDate(request.date)}</p>
                            </div>
                            <div>
                              <p className="text-slate-500 text-xs font-medium mb-0.5">Slot</p>
                              <p className="text-slate-900 font-semibold">{slot?.name}</p>
                              <p className="text-slate-500 text-xs">{slot?.time}</p>
                            </div>
                            <div>
                              <p className="text-slate-500 text-xs font-medium mb-0.5">Cohort</p>
                              <p className="text-slate-900 font-semibold truncate">{cohort?.name}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Toggle button */}
                      <button className="p-2 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0">
                        {isExpanded ? <ChevronUp className="text-slate-600" size={20} /> : <ChevronDown className="text-slate-600" size={20} />}
                      </button>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="border-t border-slate-200 p-5 bg-slate-50">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Preview */}
                        <div>
                          <CampaignPreview
                            creative={request.creative}
                            contentText={request.content_text}
                            contentCta={request.content_cta}
                            brandColor={brand?.color}
                          />
                        </div>

                        {/* Details & Actions */}
                        <div className="space-y-4">
                          {/* Content details */}
                          <div className="bg-white rounded-lg border border-slate-200 p-4">
                            <h4 className="text-sm font-bold text-slate-900 mb-3">Campaign Details</h4>
                            <div className="space-y-3 text-sm">
                              <div>
                                <p className="text-slate-500 font-medium mb-1">Message Text:</p>
                                <p className="text-slate-900">{request.content_text || 'Not provided'}</p>
                              </div>
                              <div>
                                <p className="text-slate-500 font-medium mb-1">CTA Button:</p>
                                <p className="text-slate-900">{request.content_cta || 'Not provided'}</p>
                              </div>
                              {request.drive_link && (
                                <div>
                                  <p className="text-slate-500 font-medium mb-1">Drive Link:</p>
                                  <a 
                                    href={request.drive_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm font-semibold hover:underline inline-flex items-center gap-1.5 text-teal-600 hover:text-teal-700"
                                  >
                                    🔗 Open Drive Link
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                  </a>
                                </div>
                              )}
                              {request.creative && (
                                <div>
                                  <p className="text-slate-500 font-medium mb-2">Creative:</p>
                                  <div className="flex gap-2">
                                    <a
                                      href={request.creative}
                                      download
                                      className="px-3 py-2 bg-teal-50 text-teal-600 rounded-lg text-xs font-semibold hover:bg-teal-100 transition-colors"
                                    >
                                      📥 Download
                                    </a>
                                    <button
                                      onClick={() => {
                                        navigator.clipboard.writeText(request.creative);
                                        showToast('Creative URL copied!', 'success');
                                      }}
                                      className="px-3 py-2 bg-teal-50 text-teal-600 rounded-lg text-xs font-semibold hover:bg-teal-100 transition-colors"
                                    >
                                      📋 Copy URL
                                    </button>
                                  </div>
                                </div>
                              )}
                              {request.note && (
                                <div>
                                  <p className="text-slate-500 font-medium mb-1">Notes:</p>
                                  <p className="text-slate-600 italic">{request.note}</p>
                                </div>
                              )}
                              <div>
                                <p className="text-slate-500 font-medium mb-1">Submitted:</p>
                                <p className="text-slate-900">{formatDate(request.created_at)}</p>
                              </div>
                            </div>
                          </div>

                          {/* A/B Testing */}
                          {request.ab_testing && (
                            <div className="bg-white rounded-lg border border-slate-200 p-4">
                              <h4 className="text-sm font-bold text-slate-900 mb-3">A/B Testing Variations</h4>
                              
                              <div className="space-y-3">
                                {/* Variation 1 */}
                                <div className="bg-slate-50 rounded-lg p-3">
                                  <p className="text-xs font-bold text-slate-700 mb-2">Variation 1</p>
                                  {request.ab_variation1_creative && (
                                    <div className="mb-2">
                                      <p className="text-xs text-slate-500 mb-1">Creative:</p>
                                      <img 
                                        src={request.ab_variation1_creative} 
                                        alt="Variation 1" 
                                        className="w-full h-32 object-cover rounded border border-slate-200"
                                      />
                                    </div>
                                  )}
                                  {request.ab_variation1_text && (
                                    <div>
                                      <p className="text-xs text-slate-500 mb-1">Text:</p>
                                      <p className="text-xs text-slate-900">{request.ab_variation1_text}</p>
                                    </div>
                                  )}
                                  {request.ab_variation1_cta && (
                                    <div className="mt-2">
                                      <p className="text-xs text-slate-500 mb-1">CTA:</p>
                                      <p className="text-xs text-slate-900">{request.ab_variation1_cta}</p>
                                    </div>
                                  )}
                                </div>

                                {/* Variation 2 */}
                                <div className="bg-slate-50 rounded-lg p-3">
                                  <p className="text-xs font-bold text-slate-700 mb-2">Variation 2</p>
                                  {request.ab_variation2_creative && (
                                    <div className="mb-2">
                                      <p className="text-xs text-slate-500 mb-1">Creative:</p>
                                      <img 
                                        src={request.ab_variation2_creative} 
                                        alt="Variation 2" 
                                        className="w-full h-32 object-cover rounded border border-slate-200"
                                      />
                                    </div>
                                  )}
                                  {request.ab_variation2_text && (
                                    <div>
                                      <p className="text-xs text-slate-500 mb-1">Text:</p>
                                      <p className="text-xs text-slate-900">{request.ab_variation2_text}</p>
                                    </div>
                                  )}
                                  {request.ab_variation2_cta && (
                                    <div className="mt-2">
                                      <p className="text-xs text-slate-500 mb-1">CTA:</p>
                                      <p className="text-xs text-slate-900">{request.ab_variation2_cta}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Actions */}
                          {request.status === 'Pending' && (
                            <div className="flex gap-3">
                              <button
                                onClick={() => handleApprove(request.id)}
                                className="flex-1 h-11 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg transition-all flex items-center justify-center gap-2 shadow-sm"
                                style={{ backgroundColor: '#2EB4AC' }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#26A199'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = '#2EB4AC'}
                              >
                                <Check size={18} />
                                Approve
                              </button>
                              <button
                                onClick={() => setShowRejectModal(request.id)}
                                className="flex-1 h-11 bg-red-50 hover:bg-red-100 text-red-600 font-semibold rounded-lg transition-all flex items-center justify-center gap-2 border border-red-200"
                              >
                                <X size={18} />
                                Reject
                              </button>
                            </div>
                          )}

                          {request.status === 'Approved' && request.date && isDateInFuture(request.date) && (
                            <button
                              onClick={() => setDeleteConfirm(request.id)}
                              className="w-full h-11 bg-red-50 hover:bg-red-100 text-red-600 font-semibold rounded-lg transition-all flex items-center justify-center gap-2 border border-red-200"
                            >
                              <Trash2 size={18} />
                              Cancel Slot
                            </button>
                          )}

                          {request.status === 'Rejected' && request.rejection_reason && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                              <p className="text-xs font-bold text-red-700 mb-1">Rejection Reason:</p>
                              <p className="text-sm text-red-600">{request.rejection_reason}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Reject Request</h3>
            <p className="text-sm text-slate-600 mb-4">Please provide a reason for rejection (optional)</p>
            <textarea
              value={rejectionReason[showRejectModal] || ''}
              onChange={(e) => setRejectionReason({ ...rejectionReason, [showRejectModal]: e.target.value })}
              placeholder="Enter rejection reason..."
              rows={4}
              className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-none text-slate-900 placeholder:text-slate-400"
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setShowRejectModal(null)}
                className="flex-1 h-11 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg transition-all"
              >
                Cancel
              </button>
              <button
                onClick={() => handleReject(showRejectModal)}
                className="flex-1 h-11 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-all"
              >
                Reject Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-slate-900 mb-2">Cancel Campaign Slot</h3>
            <p className="text-sm text-slate-600 mb-6">
              Are you sure you want to cancel this campaign slot? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 h-11 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg transition-all"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteCampaign(deleteConfirm)}
                className="flex-1 h-11 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-all"
              >
                Yes, Cancel Slot
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestQueue;